import { Card } from '@shared/components/ui';
import { Database, Folder, Clock, Calendar, HardDrive, Check, Loader2, Server } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Template } from './TemplateSelection';

interface ReviewCreateProps {
  template: Template;
  data: {
    name: string;
    agent_id: string;
    host?: string;
    port?: number;
    username?: string;
    database?: string;
    path?: string;
    useVSS?: boolean;
  };
  onCreate: () => Promise<void>;
  onBack: () => void;
  isCreating: boolean;
}

interface StorageDestination {
  id: string;
  name: string;
  type: string;
  is_default: boolean;
}

interface Agent {
  id: string;
  name: string;
  hostname?: string;
}

export function ReviewCreate({ template, data, onCreate, onBack, isCreating }: ReviewCreateProps) {
  const { t } = useTranslation('backups');
  const [destinations, setDestinations] = useState<StorageDestination[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const token = localStorage.getItem('auth_token');
      const [storageRes, agentsRes] = await Promise.all([
        fetch('/api/v1/storage', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/v1/agents', { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (!storageRes.ok || !agentsRes.ok) {
        throw new Error('Failed to load backup review data');
      }

      const storageData = await storageRes.json();
      const agentsData = await agentsRes.json();

      setDestinations(Array.isArray(storageData) ? storageData : storageData.destinations || []);
      setAgents(Array.isArray(agentsData) ? agentsData : agentsData.agents || []);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const selectedAgent = agents.find((a) => a.id === data.agent_id);
  const defaultStorage =
    destinations.find((d) => d.type.toUpperCase() === 'PLATFORM') ||
    destinations.find((d) => d.is_default) ||
    destinations[0];
  const reviewUnavailable = !selectedAgent || defaultStorage?.type.toUpperCase() !== 'PLATFORM';

  const getScheduleDisplay = () => {
    if (template.scheduleType === 'CUSTOM' && template.cronExpression) {
      // Parse cron to display human-readable schedule
      if (template.cronExpression === '0 12,18 * * *') {
        return t('simpleWizard.review.schedule2x');
      }
      if (template.cronExpression === '0 6,12,18 * * *') {
        return t('simpleWizard.review.schedule3x');
      }
      return template.cronExpression;
    }
    return '';
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
          {t('simpleWizard.review.title')}
        </h3>
        <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
          {t('simpleWizard.review.description')}
        </p>
      </div>

      {loading && (
        <p
          role="status"
          className="text-sm text-text-muted-light dark:text-text-muted-dark"
          aria-live="polite"
        >
          {t('simpleWizard.review.loading')}
        </p>
      )}

      {!loading && (loadFailed || reviewUnavailable) && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-state-danger-light/30 dark:border-state-danger-dark/30 bg-state-danger-bg-light dark:bg-state-danger-bg-dark p-4"
        >
          <p className="text-sm text-state-danger-light dark:text-state-danger-dark">
            {loadFailed
              ? t('simpleWizard.errors.reviewLoadFailed')
              : t('simpleWizard.errors.reviewDataUnavailable')}
          </p>
          <button
            type="button"
            onClick={() => void fetchData()}
            className="min-h-11 px-3 text-sm font-semibold text-brand-700 dark:text-brand-300 underline underline-offset-2"
          >
            {t('simpleWizard.buttons.retry')}
          </button>
        </div>
      )}

      <Card className="p-5 space-y-5">
        <div className="flex items-start space-x-4 pb-5 border-b border-surface-border-light dark:border-surface-border-dark">
          <div className="p-3 bg-accent-soft-light dark:bg-accent-soft-dark text-brand-600 dark:text-brand-400 rounded-xl">
            {template.type === 'POSTGRESQL' ? <Database size={24} /> : <Folder size={24} />}
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-slate-900 dark:text-white text-lg">{data.name}</h4>
            <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
              {t('simpleWizard.review.template')}:{' '}
              {t(`simpleWizard.template.items.${template.id}.name`)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Server
                size={18}
                className="text-text-subtle-light dark:text-text-subtle-dark mt-0.5"
              />
              <div>
                <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                  {t('simpleWizard.review.agent')}
                </p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  {loading ? '...' : selectedAgent?.name || t('simpleWizard.review.unknown')}
                </p>
                {selectedAgent?.hostname && (
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                    {selectedAgent.hostname}
                  </p>
                )}
              </div>
            </div>

            {template.type === 'POSTGRESQL' ? (
              <>
                <div className="flex items-start space-x-3">
                  <Database
                    size={18}
                    className="text-text-subtle-light dark:text-text-subtle-dark mt-0.5"
                  />
                  <div>
                    <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                      {t('simpleWizard.review.database')}
                    </p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {data.database}
                    </p>
                    <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                      {data.host}:{data.port}
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <Check
                    size={18}
                    className="text-text-subtle-light dark:text-text-subtle-dark mt-0.5"
                  />
                  <div>
                    <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                      {t('simpleWizard.review.user')}
                    </p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {data.username}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start space-x-3">
                  <Folder
                    size={18}
                    className="text-text-subtle-light dark:text-text-subtle-dark mt-0.5"
                  />
                  <div>
                    <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                      {t('simpleWizard.review.path')}
                    </p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white break-all">
                      {data.path}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Check
                    size={18}
                    className={
                      (data.useVSS ?? true)
                        ? 'text-state-ok-light dark:text-state-ok-dark mt-0.5'
                        : 'text-text-subtle-light dark:text-text-subtle-dark mt-0.5'
                    }
                  />
                  <div>
                    <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                      {t('simpleWizard.details.vssTitle')}
                    </p>
                    <p className="text-sm font-semibold text-slate-900 dark:text-white">
                      {t(
                        (data.useVSS ?? true)
                          ? 'simpleWizard.review.enabled'
                          : 'simpleWizard.review.disabled'
                      )}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Clock
                size={18}
                className="text-text-subtle-light dark:text-text-subtle-dark mt-0.5"
              />
              <div>
                <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                  {t('simpleWizard.review.schedule')}
                </p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  {getScheduleDisplay()}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <Calendar
                size={18}
                className="text-text-subtle-light dark:text-text-subtle-dark mt-0.5"
              />
              <div>
                <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                  {t('simpleWizard.review.retention')}
                </p>
                <p className="text-sm text-slate-700 dark:text-slate-300">
                  {t('simpleWizard.review.retentionDaily', { count: template.retentionDaily })} •{' '}
                  {t('simpleWizard.review.retentionWeekly', { count: template.retentionWeekly })} •{' '}
                  {t('simpleWizard.review.retentionMonthly', { count: template.retentionMonthly })}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <HardDrive
                size={18}
                className="text-text-subtle-light dark:text-text-subtle-dark mt-0.5"
              />
              <div>
                <p className="text-xs font-medium text-text-muted-light dark:text-text-muted-dark uppercase">
                  {t('simpleWizard.review.storage')}
                </p>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">
                  {loading
                    ? '...'
                    : defaultStorage?.name || t('simpleWizard.review.defaultStorage')}
                </p>
                {defaultStorage?.type && (
                  <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                    {defaultStorage.type}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="p-4 bg-state-info-bg-light dark:bg-state-info-bg-dark border border-state-info-light/20 dark:border-state-info-dark/20 rounded-xl">
        <p className="text-sm text-state-info-light dark:text-state-info-dark">
          {t('simpleWizard.review.notice')}
        </p>
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={onBack}
          disabled={isCreating}
          className="px-6 py-2.5 bg-surface-2-light dark:bg-surface-2-dark hover:bg-surface-border-light dark:hover:bg-surface-border-dark text-slate-700 dark:text-slate-300 font-bold rounded-xl transition-colors disabled:opacity-50"
        >
          {t('simpleWizard.buttons.back')}
        </button>
        <button
          type="button"
          onClick={onCreate}
          disabled={isCreating || loading || loadFailed || reviewUnavailable}
          className="px-6 py-2.5 bg-state-ok-light hover:bg-state-ok-light/90 disabled:bg-surface-border-light dark:disabled:bg-surface-border-dark disabled:cursor-not-allowed text-white font-bold rounded-xl transition-colors flex items-center space-x-2"
        >
          {isCreating ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              <span>{t('simpleWizard.buttons.creating')}</span>
            </>
          ) : (
            <>
              <Check size={18} />
              <span>{t('simpleWizard.buttons.create')}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}
